import { SparklesIcon } from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { HAS_FREE_PLAN } from '@/constants/common';
import { Preset } from '@/constants/presets';
import {
  SOCIAL_STORIES_BOT_URL,
  SOCIAL_STORIES_PROMO_IMAGE,
} from '@/constants/social-stories';

import LandingPageNavigation from './layout/LandingPageNavigation';
import ButtonLink from './links/ButtonLink';
import LogosCloud from './LogosCloud';
const TemplateForm = dynamic(() => import('./PublicTemplateForm'));

interface TemplatesHeroSectionProps {
  preset: Preset;
}

interface TemplatePreviewSample {
  file: string;
  label: string;
  alt?: string;
}

interface TemplatePreviewSampleSet {
  heading: string;
  ariaLabel: string;
  baseUrl: string;
  samples: TemplatePreviewSample[];
  hideLastOnMobile?: boolean;
  aspectClassName?: string;
  frameClassName?: string;
  imageClassName?: string;
}

const LANDING_MEDIA_BASE_URL = 'https://media.easy-peasy.ai/landing';

// Visual template samples are hosted on R2 to keep the Next.js deployment
// artifact small.
const TEMPLATE_PREVIEW_SAMPLE_SETS: Partial<
  Record<Preset['slug'], TemplatePreviewSampleSet>
> = {
  'worksheet-generator': {
    heading: 'Print-ready worksheets across every subject',
    ariaLabel: 'worksheet',
    baseUrl: `${LANDING_MEDIA_BASE_URL}/worksheets`,
    hideLastOnMobile: true,
    aspectClassName: 'aspect-[2/3]',
    frameClassName: 'bg-gray-50 dark:bg-slate-900',
    imageClassName: 'object-cover',
    samples: [
      { file: 'fractions-pizza.png', label: '3rd Grade · Fractions' },
      { file: 'reading-comprehension.png', label: '4th Grade · Reading' },
      { file: 'kindergarten-counting.png', label: 'Kindergarten · Counting' },
      { file: 'parts-of-a-plant.png', label: 'Elementary · Science' },
      { file: 'short-vowel-sounds.png', label: 'Kindergarten · Phonics' },
    ],
  },
  'linkedin-carousel-generator': {
    heading: 'LinkedIn carousel styles you can create',
    ariaLabel: 'LinkedIn carousel slide',
    baseUrl: `${LANDING_MEDIA_BASE_URL}/linkedin-carousel-generator`,
    hideLastOnMobile: true,
    aspectClassName: 'aspect-[4/5]',
    frameClassName: 'bg-slate-100 dark:bg-slate-900',
    imageClassName: 'object-cover',
    samples: [
      {
        file: 'imagegen-founder-photo-v4.png',
        label: 'Founder photo',
        alt: 'LinkedIn carousel slide with a realistic founder photo and editorial hook',
      },
      {
        file: 'imagegen-workshop-v4.png',
        label: 'Workshop',
        alt: 'LinkedIn carousel slide with a workshop photo and launch plan collage',
      },
      {
        file: 'imagegen-data-frame-v4.png',
        label: 'Data frame',
        alt: 'LinkedIn carousel data visualization slide about why buyers ignore demos',
      },
      {
        file: 'imagegen-interview-v4.png',
        label: 'Interview',
        alt: 'LinkedIn carousel micro-interview slide with an editorial portrait',
      },
      {
        file: 'imagegen-cta-v4.png',
        label: 'CTA finale',
        alt: 'LinkedIn carousel final slide with a save prompt and clear call to action',
      },
    ],
  },
  'instagram-carousel-generator': {
    heading: 'Instagram carousel styles you can create',
    ariaLabel: 'Instagram carousel slide',
    baseUrl: `${LANDING_MEDIA_BASE_URL}/instagram-carousel-generator`,
    hideLastOnMobile: true,
    aspectClassName: 'aspect-[4/5]',
    frameClassName: 'bg-slate-100 dark:bg-slate-900',
    imageClassName: 'object-cover',
    samples: [
      {
        file: 'imagegen-morning-reset-v1.png',
        label: 'Lifestyle hook',
        alt: 'Instagram carousel lifestyle slide about morning habits',
      },
      {
        file: 'imagegen-meal-prep-v1.png',
        label: 'Food guide',
        alt: 'Instagram carousel food guide slide with meal prep photography',
      },
      {
        file: 'imagegen-style-note-v1.png',
        label: 'Style note',
        alt: 'Instagram carousel fashion and beauty moodboard slide',
      },
      {
        file: 'imagegen-city-guide-v1.png',
        label: 'Travel guide',
        alt: 'Instagram carousel travel guide slide with photo journal collage',
      },
      {
        file: 'imagegen-save-this-v1.png',
        label: 'Save CTA',
        alt: 'Instagram carousel final slide with a save and share call to action',
      },
    ],
  },
};

interface CardTemplateSample {
  file: string;
  label: string;
  alt: string;
}

interface CardTemplateSampleSet {
  heading: string;
  ariaLabel: string;
  baseUrl: string;
  samples: CardTemplateSample[];
  hideLastOnMobile?: boolean;
}

// Generated card samples are hosted on R2 at media.easy-peasy.ai/landing/<slug>/
// (not bundled in the Next.js app to keep the deployment artifact small).
const CARD_SAMPLES_BASE_URL = LANDING_MEDIA_BASE_URL;
const CARD_TEMPLATE_SAMPLE_SETS: Partial<
  Record<Preset['slug'], CardTemplateSampleSet>
> = {
  'birthday-card-creator': {
    heading: 'Birthday card styles you can create',
    ariaLabel: 'birthday card',
    baseUrl: `${CARD_SAMPLES_BASE_URL}/birthday-card-creator`,
    hideLastOnMobile: true,
    samples: [
      {
        file: 'elegant-floral-birthday-card.webp',
        label: 'Elegant floral',
        alt: 'Elegant floral Happy Birthday card with watercolor peonies',
      },
      {
        file: 'kids-dinosaur-birthday-card.webp',
        label: 'Kids party',
        alt: 'Playful dinosaur Happy Birthday card for kids',
      },
      {
        file: 'luxury-milestone-birthday-card.webp',
        label: 'Milestone birthday',
        alt: 'Luxury black and gold 30th birthday card',
      },
      {
        file: 'watercolor-puppy-birthday-card.webp',
        label: 'Pet lover',
        alt: 'Watercolor puppy Happy Birthday card with pastel balloons',
      },
      {
        file: 'retro-party-birthday-card.webp',
        label: 'Retro party',
        alt: 'Retro Birthday Vibes card with disco ball and streamers',
      },
    ],
  },
  'greetings-generator': {
    heading: 'Greeting card styles you can create',
    ariaLabel: 'greeting card',
    baseUrl: `${CARD_SAMPLES_BASE_URL}/greetings-generator`,
    samples: [
      {
        file: 'cute-birthday-greeting-card.webp',
        label: 'Cute',
        alt: 'Cute Happy Birthday greeting card with balloons and gifts',
      },
      {
        file: 'elegant-congratulations-card.webp',
        label: 'Elegant',
        alt: 'Elegant Congratulations greeting card with gold botanicals',
      },
      {
        file: 'festive-christmas-card.webp',
        label: 'Festive',
        alt: 'Festive Merry Christmas greeting card with ornaments',
      },
      {
        file: 'floral-mothers-day-card.webp',
        label: 'Floral',
        alt: "Floral Happy Mother's Day greeting card with watercolor roses",
      },
      {
        file: 'funny-congratulations-card.webp',
        label: 'Funny',
        alt: 'Funny You Did It greeting card with a trophy illustration',
      },
      {
        file: 'minimalist-new-year-card.webp',
        label: 'Minimalist',
        alt: 'Minimalist Happy New Year greeting card with clean typography',
      },
    ],
  },
  'valentine-creator': {
    heading: 'Valentine card styles you can create',
    ariaLabel: "Valentine's Day card",
    baseUrl: `${CARD_SAMPLES_BASE_URL}/valentine-creator`,
    hideLastOnMobile: true,
    samples: [
      {
        file: 'romantic-valentine-card.webp',
        label: 'Romantic',
        alt: "Romantic Happy Valentine's Day card with roses and ribbons",
      },
      {
        file: 'cute-valentine-card.webp',
        label: 'Cute',
        alt: "Cute Happy Valentine's Day card with lovebirds",
      },
      {
        file: 'elegant-valentine-card.webp',
        label: 'Elegant',
        alt: "Elegant Happy Valentine's Day card with gold accents",
      },
      {
        file: 'funny-valentine-card.webp',
        label: 'Funny',
        alt: "Funny Happy Valentine's Day card with a pizza illustration",
      },
      {
        file: 'floral-valentine-card.webp',
        label: 'Floral',
        alt: "Floral Happy Valentine's Day card with watercolor roses",
      },
    ],
  },
  'birthday-invitation-generator': {
    heading: 'Birthday invitation styles you can create',
    ariaLabel: 'birthday invitation',
    baseUrl: `${CARD_SAMPLES_BASE_URL}/birthday-invitation-generator`,
    hideLastOnMobile: true,
    samples: [
      {
        file: 'cute-birthday-invitation.webp',
        label: 'Cute',
        alt: 'Cute birthday invitation with pastel balloons and gifts',
      },
      {
        file: 'festive-birthday-invitation.webp',
        label: 'Festive',
        alt: 'Festive birthday invitation with cake, balloons and confetti',
      },
      {
        file: 'elegant-birthday-invitation.webp',
        label: 'Elegant',
        alt: 'Elegant birthday invitation with black ribbon and gold accents',
      },
      {
        file: 'kids-birthday-invitation.webp',
        label: 'Kids',
        alt: 'Kids birthday invitation with a rocket and space party theme',
      },
      {
        file: 'minimalist-birthday-invitation.webp',
        label: 'Minimalist',
        alt: 'Minimalist birthday invitation with clean typography',
      },
    ],
  },
  'thank-you-note-generator': {
    heading: 'Thank-you card styles you can create',
    ariaLabel: 'thank-you card',
    baseUrl: `${CARD_SAMPLES_BASE_URL}/thank-you-note-generator`,
    samples: [
      {
        file: 'elegant-thank-you-card.webp',
        label: 'Elegant',
        alt: 'Elegant Thank You card with gold botanicals',
      },
      {
        file: 'floral-thank-you-card.webp',
        label: 'Floral',
        alt: 'Floral Thank You card with watercolor flowers',
      },
      {
        file: 'cute-thank-you-card.webp',
        label: 'Cute',
        alt: 'Cute Thank You card with a smiling envelope illustration',
      },
      {
        file: 'minimalist-thank-you-card.webp',
        label: 'Minimalist',
        alt: 'Minimalist Thank You card with clean typography',
      },
    ],
  },
};

const getSampleGridClassName = (sampleCount: number) =>
  clsxm(
    'grid grid-cols-2 gap-3 md:gap-4',
    sampleCount === 4 && 'sm:grid-cols-2 md:grid-cols-4',
    sampleCount === 5 && 'sm:grid-cols-3 md:grid-cols-5',
    sampleCount === 6 && 'sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6',
  );

const TemplatesHeroSection = ({ preset }: TemplatesHeroSectionProps) => {
  const { t } = useLocale('tools');
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const previewSampleSet = TEMPLATE_PREVIEW_SAMPLE_SETS[preset.slug];
  const cardSampleSet = CARD_TEMPLATE_SAMPLE_SETS[preset.slug];

  // Close lightbox on Escape, lock body scroll while open. Only attached
  // when the lightbox is actually open — zero overhead otherwise.
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxSrc(null);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [lightboxSrc]);

  return (
    <div
      className={clsxm(
        preset.isFree ? 'bg-gray-50' : 'bg-white',
        'relative   dark:bg-slate-900',
      )}
    >
      <LandingPageNavigation skipUserContext />

      <section className='pb-8 sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32'>
        <div className='relative isolate'>
          <svg
            className='absolute inset-x-0 top-0 -z-10 h-[64rem] w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)] dark:stroke-gray-800'
            aria-hidden='true'
          >
            <defs>
              <pattern
                id='1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84'
                width={200}
                height={200}
                x='50%'
                y={-1}
                patternUnits='userSpaceOnUse'
              >
                <path d='M.5 200V.5H200' fill='none' />
              </pattern>
            </defs>
            <svg
              x='50%'
              y={-1}
              className='overflow-visible fill-gray-50 dark:hidden'
            >
              <path
                d='M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z'
                strokeWidth={0}
              />
            </svg>
            <rect
              width='100%'
              height='100%'
              strokeWidth={0}
              fill='url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)'
            />
          </svg>
          <div
            className='absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48'
            aria-hidden='true'
          >
            <div
              className='aspect-[801/1036] w-[50.0625rem] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30'
              style={{
                clipPath:
                  'polygon(63.1% 29.5%, 100% 17.1%, 76.6% 3%, 48.4% 0%, 44.6% 4.7%, 54.5% 25.3%, 59.8% 49%, 55.2% 57.8%, 44.4% 57.2%, 27.8% 47.9%, 35.1% 81.5%, 0% 97.7%, 39.2% 100%, 35.2% 81.4%, 97.2% 52.8%, 63.1% 29.5%)',
              }}
            />
          </div>

          <div className='mx-auto mt-10 flex h-full flex-col items-center justify-center sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28'>
            <div className='w-full max-w-4xl text-center xl:max-w-5xl '>
              {[
                'progress-note-generator',
                'dap-note-generator',
                'soap-note-generator',
                'smart-goal-generator',
                'iep-goal-generator',
                'aba-notes-generator',
                'birp-note-generator',
              ].includes(preset.slug) ? (
                <div className='mx-auto mt-6 flex max-w-2xl flex-wrap items-center justify-center gap-6'>
                  <div className='flex items-center gap-3'>
                    <Image
                      src='/images/hipaa.png'
                      width={32}
                      height={32}
                      alt='HIPAA Compliant'
                      title='HIPAA Compliant'
                    />
                    <span className='text-sm font-medium text-gray-500 dark:text-slate-400'>
                      HIPAA Compliant
                    </span>
                  </div>
                  <div className='flex items-center gap-3'>
                    <Image
                      src='/images/iso-27001.png'
                      width={32}
                      height={32}
                      alt='ISO 27001 Certified'
                      title='ISO 27001 Certified'
                    />
                    <span className='text-sm font-medium text-gray-500 dark:text-slate-400'>
                      ISO 27001 Certified
                    </span>
                  </div>

                  <div className='flex items-center gap-3'>
                    <Image
                      src='/images/soc2.png'
                      width={32}
                      height={32}
                      alt='SOC2 Certified'
                      title='SOC2 Certified'
                    />
                    <span className='text-sm font-medium text-gray-500 dark:text-slate-400'>
                      SOC2 Certified
                    </span>
                  </div>
                </div>
              ) : null}

              {preset.slug === 'social-stories-generator' && (
                <Link
                  href={SOCIAL_STORIES_BOT_URL}
                  className='mt-16 inline-flex items-center gap-2.5 rounded-full border border-indigo-200 bg-indigo-50 py-1.5 pl-1.5 pr-4 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50'
                >
                  <Image
                    src={SOCIAL_STORIES_PROMO_IMAGE}
                    alt='AI illustrated social story example'
                    width={64}
                    height={64}
                    className='rounded-full'
                  />
                  New: Create a fully illustrated PDF picture book with AI
                  <span aria-hidden='true'>&rarr;</span>
                </Link>
              )}

              <h1
                className={clsxm(
                  [
                    'progress-note-generator',
                    'dap-note-generator',
                    'soap-note-generator',
                    'iep-goal-generator',
                    'aba-notes-generator',
                    'birp-note-generator',
                    'smart-goal-generator',
                    'social-stories-generator',
                  ].includes(preset.slug)
                    ? 'mt-5'
                    : 'mt-32',
                  'px-2 text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-200 sm:text-5xl md:text-6xl',
                )}
              >
                <span className='block xl:inline'>
                  {t(preset.title) || preset.title}
                </span>{' '}
              </h1>
              <p className='mx-auto mt-3 max-w-4xl px-2 text-2xl text-gray-500 dark:text-slate-400 sm:mt-5 md:mt-5'>
                {t(preset.description) || preset.description}
              </p>

              {previewSampleSet && (
                <div className='mx-auto mt-10 w-full max-w-6xl px-2'>
                  <p className='mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400'>
                    {t(previewSampleSet.heading)}
                  </p>
                  <div
                    className={getSampleGridClassName(
                      previewSampleSet.samples.length,
                    )}
                  >
                    {previewSampleSet.samples.map((sample, idx) => {
                      const src = `${previewSampleSet.baseUrl}/${sample.file}`;
                      const hideOnMobile =
                        previewSampleSet.hideLastOnMobile &&
                        idx === previewSampleSet.samples.length - 1;
                      const sampleLabel = t(sample.label);
                      return (
                        <button
                          key={sample.file}
                          type='button'
                          onClick={() => setLightboxSrc(src)}
                          aria-label={`${t('Preview')} ${sampleLabel} ${t(previewSampleSet.ariaLabel)}`}
                          className={clsxm(
                            'group cursor-zoom-in overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-800',
                            hideOnMobile && 'hidden sm:block',
                          )}
                        >
                          <div
                            className={clsxm(
                              'relative w-full overflow-hidden',
                              previewSampleSet.aspectClassName,
                              previewSampleSet.frameClassName,
                            )}
                          >
                            <Image
                              src={src}
                              alt={sample.alt || sample.label}
                              fill
                              className={clsxm(
                                'transition-transform duration-300 group-hover:scale-105',
                                previewSampleSet.imageClassName,
                              )}
                              sizes='(max-width: 640px) 50vw, (max-width: 768px) 33vw, 20vw'
                            />
                          </div>
                          <div className='border-t border-gray-100 px-2 py-2 text-center text-xs font-medium text-gray-700 dark:border-slate-700 dark:text-slate-300'>
                            {sampleLabel}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {cardSampleSet && (
                <div className='mx-auto mt-10 w-full max-w-6xl px-2'>
                  <p className='mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400'>
                    {t(cardSampleSet.heading)}
                  </p>
                  <div
                    className={getSampleGridClassName(
                      cardSampleSet.samples.length,
                    )}
                  >
                    {cardSampleSet.samples.map((sample, idx) => {
                      const src = `${cardSampleSet.baseUrl}/${sample.file}`;
                      const hideOnMobile =
                        cardSampleSet.hideLastOnMobile &&
                        idx === cardSampleSet.samples.length - 1;
                      const sampleLabel = t(sample.label);
                      return (
                        <button
                          key={sample.file}
                          type='button'
                          onClick={() => setLightboxSrc(src)}
                          aria-label={`${t('Preview')} ${sampleLabel} ${t(cardSampleSet.ariaLabel)}`}
                          className={clsxm(
                            'group cursor-zoom-in overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-800',
                            hideOnMobile && 'hidden sm:block',
                          )}
                        >
                          <div className='relative aspect-[2/3] w-full overflow-hidden bg-amber-50 dark:bg-slate-900'>
                            <Image
                              src={src}
                              alt={sample.alt}
                              fill
                              className='object-contain transition-transform duration-300 group-hover:scale-105'
                              sizes='(max-width: 640px) 50vw, (max-width: 768px) 33vw, 20vw'
                            />
                          </div>
                          <div className='border-t border-gray-100 px-2 py-2 text-center text-xs font-medium text-gray-700 dark:border-slate-700 dark:text-slate-300'>
                            {sampleLabel}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className='mt-5 w-full sm:mt-8 sm:flex sm:justify-center'>
                {!preset.isFree ? (
                  <div className='mb-5 rounded-md sm:mb-0'>
                    <ButtonLink
                      variant='dark'
                      className='rounded-md px-8 py-3 text-base font-medium md:px-10 md:py-4 md:text-lg'
                      href={
                        HAS_FREE_PLAN
                          ? `/login?preset=${encodeURIComponent(preset.loginPreset || preset.slug)}`
                          : '/#pricing'
                      }
                    >
                      <span className='rainbow flex items-center'>
                        <SparklesIcon className='mr-2 h-7 w-7 text-white' />
                        {preset.onlyForPaidPlans
                          ? t('Get started')
                          : t('Get started for free')}
                      </span>
                    </ButtonLink>
                  </div>
                ) : (
                  <div className='mx-auto w-full max-w-md text-center sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8'>
                    <TemplateForm preset={preset} />
                  </div>
                )}
              </div>
            </div>
            <div className='flex w-full flex-col flex-wrap gap-x-4 gap-y-2 text-center text-gray-500 dark:text-slate-400 sm:mt-10 sm:flex-row sm:justify-center lg:text-lg'>
              {[
                'progress-note-generator',
                'dap-note-generator',
                'soap-note-generator',
                'aba-notes-generator',
                'birp-note-generator',
              ].includes(preset.slug) ? (
                <div className='flex max-w-2xl items-center justify-center gap-2'>
                  <Image
                    src='/images/hipaa.png'
                    width={80}
                    height={80}
                    alt='HIPAA Compliant'
                    className='dark:invert'
                  />
                  <span>
                    <b>HIPAA Compliant</b>: Our platform is fully HIPAA
                    compliant, ensuring your healthcare data is protected with
                    the highest security standards.{' '}
                    <Link
                      href='/settings/baa'
                      className='font-medium text-primary-600 underline hover:text-primary-700 dark:text-primary-400'
                    >
                      Review &amp; sign your BAA
                    </Link>
                  </span>
                </div>
              ) : (
                <>
                  <div>🚀 {t('Powered by best AI models')}</div>
                  <div>🌍 {t('Supports 40+ languages')}</div>
                  <div>💳 {t('No credit card required')}</div>
                  <div>⭐ {t('5-star ratings')}</div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <LogosCloud />

      {lightboxSrc && (
        <div
          role='dialog'
          aria-modal='true'
          onClick={() => setLightboxSrc(null)}
          className='fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/85 p-4 backdrop-blur-sm'
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={lightboxSrc}
            alt=''
            className='max-h-[95vh] max-w-[95vw] rounded-lg shadow-2xl'
          />
        </div>
      )}
    </div>
  );
};

export default TemplatesHeroSection;
